import { useCallback, useEffect } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { SortChangedEvent } from '@ag-grid-community/core'
import type { RefObject } from 'react'

interface SortState {
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

export function useGridSortState<TData>(
  gridRef: RefObject<AgGridReact<TData>>,
  sort: SortState | null | undefined,
  onSortChange?: (
    sortBy: string | null,
    sortOrder: 'asc' | 'desc' | null
  ) => void
) {
  const applySortState = useCallback(() => {
    if (!gridRef.current?.api) return
    if (!sort) {
      gridRef.current.api.applyColumnState({
        defaultState: { sort: null },
      })
      return
    }
    gridRef.current.api.applyColumnState({
      state: [
        {
          colId: sort.sortBy,
          sort: sort.sortOrder === 'asc' ? 'asc' : 'desc',
        },
      ],
      defaultState: { sort: null },
    })
  }, [gridRef, sort?.sortBy, sort?.sortOrder])

  useEffect(() => {
    applySortState()
  }, [applySortState])

  const onSortChanged = useCallback(
    (event: SortChangedEvent<TData>) => {
      if (!onSortChange || !event.api) return
      const sortCol = event.api.getColumnState().find((c) => c.sort != null)
      if (sortCol?.colId && sortCol.sort) {
        onSortChange(sortCol.colId, sortCol.sort === 'asc' ? 'asc' : 'desc')
      } else {
        onSortChange(null, null)
      }
    },
    [onSortChange]
  )

  return { applySortState, onSortChanged }
}
